import { Controller } from "@hotwired/stimulus"

// The delivery-method modules of the recipient step (gives/recipients/_sending_method.html.haml).
// Mounted on .recipients next to give-recipient. Exactly one module is expanded at a time, and the
// page subtitle and the top-nav "Next" follow the selected one.
//
// Creating unique links or a magic link replaces any recipients typed into the individual module,
// so it asks first (gives/recipients/_replace_recipients_modal). Going back from links to individual gifts is
// an explicit button in the individual module, never a side effect of choosing the radio.
export default class extends Controller {
  static get targets() {
    return ["module", "subtitle", "linkForm", "expiresOn", "confirmModal", "confirmMessage"];
  }

  connect() {
    this.syncNext();
    this.expiresOnTargets.forEach((field) => this.bindExpiresOnPicker(field));
  }

  select(event) {
    this.show(event.target.closest("[data-delivery-method-target='module']"));
  }

  // Creating links replaces any recipients typed into the individual module, so confirm first
  submitLinkForm(event) {
    if (this.replaceConfirmed) { return; }

    const filledRows = this.filledIndividualRows();
    if (filledRows === 0) { return; }

    const form = event.currentTarget;
    event.preventDefault();
    this.askingForm = form;
    this.askToReplace(
      `This will remove the ${this.pluralize(filledRows, "recipient")} you added.`,
      () => { this.replaceConfirmed = true; form.requestSubmit(); }
    );
  }

  confirmReplace() {
    const onConfirm = this.pendingConfirm;
    this.closeReplaceModal();
    onConfirm?.();
  }

  // Back to the button that asked, so keyboard and screen reader users keep their place
  cancelReplace() {
    this.closeReplaceModal();
    this.askingForm?.querySelector("[type=submit]")?.focus();
  }

  // "Next" does what the selected module's call-to-action does (give-recipient#onNextClick), so a
  // module without a CTA yet leaves it disabled. Covers both the desktop link in
  // gives/_progress.html.haml and the mobile duplicate, which share the id.
  syncNext() {
    const enabled = !!this.selectedModule?.querySelector("[data-delivery-method-cta]");

    this.element.querySelectorAll("#next").forEach((next) => {
      next.classList.toggle("btn-disabled", !enabled);
      next.setAttribute("aria-disabled", String(!enabled));
    });
  }

  get selectedModule() {
    return this.moduleTargets.find((module) => module.classList.contains("selected"));
  }

  show(selected) {
    this.moduleTargets.forEach((module) => {
      const isSelected = module === selected;
      module.classList.toggle("selected", isSelected);
      module.querySelector(".sending-method-body").hidden = !isSelected;
    });

    if (this.hasSubtitleTarget) {
      this.subtitleTarget.textContent = selected.dataset.subtitle;
    }

    this.syncNext();
  }

  askToReplace(message, onConfirm) {
    this.pendingConfirm = onConfirm;
    this.confirmMessageTarget.textContent = message;
    // .modal() is a Bootstrap plugin call -- stays on jQuery (R8 allowlist). Escape closes the
    // modal without going through cancelReplace, so treat any other close as a cancel.
    $(this.confirmModalTarget)
      .one("hidden.bs.modal", () => { if (this.pendingConfirm) { this.cancelReplace(); } })
      .modal("show");
  }

  closeReplaceModal() {
    this.pendingConfirm = null;
    if ($(this.confirmModalTarget).hasClass("in")) { $(this.confirmModalTarget).modal("hide"); }
  }

  // Rows in the individual module with anything typed in, saved or not
  filledIndividualRows() {
    const rows = this.moduleNamed("individual")?.querySelectorAll(".build-recipient-row") || [];
    return Array.from(rows).filter((row) =>
      Array.from(row.querySelectorAll("input[id$='name'], input[id$='email'], input[id$='phone_number']"))
        .some((input) => input.value.trim() !== "")
    ).length;
  }

  // Date only: gift expiration is stored as a date. The backend enforces the same window.
  bindExpiresOnPicker(field) {
    const tomorrow = moment().add(1, "day").startOf("day");
    // Not window.datetimepickerDefaults: its input parser expects a date and time
    $(field).datetimepicker({
      format: "MM/DD/YYYY",
      useCurrent: false,
      minDate: tomorrow,
      maxDate: moment().add(1, "year").endOf("day"),
    });
  }

  moduleNamed(method) {
    return this.moduleTargets.find((module) => module.dataset.method === method);
  }

  pluralize(count, noun) {
    return `${count} ${noun}${count === 1 ? "" : "s"}`;
  }
}
